import React, { useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { DomainService } from '../../services/domainService';
import { DeleteService } from '../../services/deleteService';
import { AlertDialog } from '../common/AlertDialog';
import { normalizeDomain, validateDomainInput, getSitePublicUrl, getSiteDisplayAddress } from '../../utils/security';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import { SiteDomain, FormattedDnsInstruction } from '../../types';
import {
  Globe,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Copy,
  Check,
  RefreshCw,
  Server,
  Trash2,
  ExternalLink,
  Lock,
  Plus,
  ArrowRight,
  Star,
  Info,
  X,
} from 'lucide-react';

export const DomainManager: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite, reloadSites } = useSite();
  const { currentUser } = useAuth();
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<'FORM' | 'SUCCESS'>('FORM');
  const [modalAddedDomain, setModalAddedDomain] = useState<string>('');
  const [modalAddedInstructions, setModalAddedInstructions] = useState<FormattedDnsInstruction[]>([]);
  const [domainInput, setDomainInput] = useState('');
  const [autoAddWww, setAutoAddWww] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verifyingDomainId, setVerifyingDomainId] = useState<string | null>(null);
  const [removingDomainName, setRemovingDomainName] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; domainName: string; loading: boolean }>({
    open: false,
    domainName: '',
    loading: false,
  });
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [dnsCheckDetails, setDnsCheckDetails] = useState<Record<string, any>>({});
  const [persistedInstructions, setPersistedInstructions] = useState<Record<string, FormattedDnsInstruction[]>>({});
  const [activeDomainForGuide, setActiveDomainForGuide] = useState<string | null>(null);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Alan Adı Yönetimi İçin Aktif Web Sitesi Gereklidir"
        description="Özel domain yönlendirme (CNAME/A kaydı) ve SSL sertifikası tanımlamak için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini aktif yapın."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const domains: SiteDomain[] = activeSite.domains || [];

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleConnectDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!domainInput.trim()) return;

    setIsSubmitting(true);
    setFeedback(null);

    try {
      const validation = validateDomainInput(domainInput.trim());
      if (!validation.isValid) {
        setFeedback({
          type: 'error',
          text: validation.error || 'Lütfen geçerli bir alan adı giriniz (örn: sirketiniz.com).',
        });
        setIsSubmitting(false);
        return;
      }
      const clean = validation.normalized;

      const ownerId = currentUser?.uid || activeSite.ownerId || 'system';

      // 1. Add Primary Domain via POST /api/domains
      const domainRecord = await DomainService.addDomain(activeSite.id, clean, ownerId);
      const instructions = domainRecord.dnsInstructions || [];

      // Save DNS records into local state immediately so they render without delay
      setPersistedInstructions((prev) => ({
        ...prev,
        [clean]: instructions,
        ...(domainRecord.id ? { [domainRecord.id]: instructions } : {}),
      }));
      setActiveDomainForGuide(clean);

      if (domainRecord.id) {
        setDnsCheckDetails((prev) => ({
          ...prev,
          [domainRecord.id]: {
            domain: clean,
            isPropagated: false,
            dnsInstructions: instructions,
            discoveredRecords: [],
          },
        }));
      }

      // 2. Optionally add www counterpart if apex was provided
      if (autoAddWww && !clean.startsWith('www.') && clean.split('.').length === 2) {
        try {
          const wwwRecord = await DomainService.addDomain(activeSite.id, `www.${clean}`, ownerId);
          if (wwwRecord.dnsInstructions?.length) {
            setPersistedInstructions((prev) => ({
              ...prev,
              [`www.${clean}`]: wwwRecord.dnsInstructions || [],
              ...(wwwRecord.id ? { [wwwRecord.id]: wwwRecord.dnsInstructions || [] } : {}),
            }));
          }
        } catch {
          // Counterpart add is optional
        }
      }

      // 3. Switch modal to SUCCESS view showing the A and TXT records
      setModalAddedDomain(clean);
      setModalAddedInstructions(instructions);
      setModalStep('SUCCESS');

      setFeedback({
        type: 'success',
        text: `Alan adı (${clean}) başarıyla kaydedildi. Altyapımız tarafından gereken DNS kayıtları (A ve TXT) listelenmiştir. Lütfen DNS sağlayıcınıza tanımlayınız.`,
      });

      await reloadSites();
    } catch (err: any) {
      setFeedback({
        type: 'error',
        text: err.message || 'Alan adı bağlantısı oluşturulamadı. Lütfen tekrar deneyin.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyDomain = async (domainId: string, domainName: string) => {
    setVerifyingDomainId(domainId);
    setFeedback(null);

    try {
      // Real DNS query via backend with siteId for tenant isolation
      const checkResult = await DomainService.verifyDnsRecords(domainName, activeSite.id);
      setDnsCheckDetails((prev) => ({ ...prev, [domainId]: checkResult }));

      const verified = await DomainService.verifyAndActivateDomain(activeSite.id, domainId);

      if (verified.status === 'ACTIVE') {
        setFeedback({
          type: 'success',
          text: `Tebrikler! ${domainName} DNS kayıtları ve 256-bit Let's Encrypt SSL sertifikası başarıyla doğrulandı ve aktif edildi.`,
        });
      } else {
        setFeedback({
          type: 'error',
          text: `${domainName}: ${checkResult.error || 'DNS kayıtları henüz yönlendirilmemiş. DNS yayılımı sağlayıcınıza göre 5-30 dakika sürebilir.'}`,
        });
      }

      await reloadSites();
    } catch (err: any) {
      setFeedback({
        type: 'error',
        text: err.message || 'Doğrulama esnasında hata oluştu.',
      });
    } finally {
      setVerifyingDomainId(null);
    }
  };

  const handleSetPrimary = async (domainId: string, domainName: string) => {
    try {
      await DomainService.setPrimaryDomain(activeSite.id, domainId);
      setFeedback({
        type: 'success',
        text: `${domainName} birincil (canonical) alan adı olarak ayarlandı. Arama motorları bu adrese yönlendirilecek.`,
      });
      await reloadSites();
    } catch (err: any) {
      setFeedback({
        type: 'error',
        text: err.message || 'Birincil alan adı güncellenirken hata oluştu.',
      });
    }
  };

  const handleRemoveDomain = (domainName: string) => {
    setDeleteDialog({
      open: true,
      domainName,
      loading: false,
    });
  };

  const handleExecuteRemoveDomain = async () => {
    if (!activeSite || !deleteDialog.domainName || deleteDialog.loading) return;
    const domainName = deleteDialog.domainName;

    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    setRemovingDomainName(domainName);
    setFeedback(null);

    try {
      const res = await DeleteService.deleteDomain(activeSite.id, domainName);
      if (res.success) {
        setFeedback({
          type: 'success',
          text: `${domainName} alan adı başarıyla kaldırıldı.`,
        });
        await reloadSites();
        setDeleteDialog({ open: false, domainName: '', loading: false });
      } else {
        setFeedback({
          type: 'error',
          text: res.error || 'Alan adı kaldırılırken hata oluştu.',
        });
        setDeleteDialog((prev) => ({ ...prev, loading: false }));
      }
    } catch (err: any) {
      setFeedback({
        type: 'error',
        text: err.message || 'Alan adı kaldırılırken hata oluştu.',
      });
      setDeleteDialog((prev) => ({ ...prev, loading: false }));
    } finally {
      setRemovingDomainName(null);
    }
  };

  const getStatusBadge = (domain: SiteDomain) => {
    if (domain.status === 'ACTIVE' && domain.sslStatus === 'ACTIVE') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
          Bağlandı & SSL Aktif
        </span>
      );
    }

    if (domain.status === 'VERIFYING') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 flex items-center gap-1.5 animate-pulse">
          <RefreshCw className="w-3.5 h-3.5 text-blue-600 animate-spin" />
          Sorgulanıyor...
        </span>
      );
    }

    if (domain.status === 'ACTIVE' && domain.sslStatus === 'PENDING') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800 flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5 text-purple-600" />
          SSL Hazırlanıyor
        </span>
      );
    }

    if (domain.status === 'FAILED') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800 flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 text-rose-600" />
          DNS Bekleniyor / Hata
        </span>
      );
    }

    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 flex items-center gap-1.5">
        <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
        DNS Yönlendirmesi Bekleniyor
      </span>
    );
  };

  const primaryDomainRecord = domains.find((d) => d.isPrimary) || domains[0];
  const selectedDomainName = activeDomainForGuide || primaryDomainRecord?.domain;
  const currentDomainRecord =
    domains.find((d) => d.domain === selectedDomainName || d.id === selectedDomainName) || primaryDomainRecord;

  const currentCheckInfo = currentDomainRecord ? dnsCheckDetails[currentDomainRecord.id] : null;

  const guideInstructions: FormattedDnsInstruction[] =
    (selectedDomainName && persistedInstructions[selectedDomainName]?.length)
      ? persistedInstructions[selectedDomainName]
      : (currentDomainRecord?.id && persistedInstructions[currentDomainRecord.id]?.length)
      ? persistedInstructions[currentDomainRecord.id]
      : (currentCheckInfo?.dnsInstructions && currentCheckInfo.dnsInstructions.length > 0)
      ? currentCheckInfo.dnsInstructions
      : (currentDomainRecord?.dnsInstructions && currentDomainRecord.dnsInstructions.length > 0)
      ? currentDomainRecord.dnsInstructions
      : (modalAddedInstructions.length > 0)
      ? modalAddedInstructions
      : [];

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in">
      {/* Header & Primary CTA */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-teal-600">
            <Globe className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Alan Adı & DNS Altyapısı</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Özel Domain & SSL Yönetimi
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Sitenize özel alan adlarınızı (.com, .net, .org, .com.tr) bağlayın, birincil alan adını seçin ve otomatik 256-bit SSL ile güvenle yayınlayın.
          </p>
        </div>

        <button
          onClick={() => {
            setIsConnectModalOpen(true);
            setModalStep('FORM');
            setDomainInput('');
          }}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-500 text-white font-bold text-sm shadow-sm transition self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Özel Domain Bağla</span>
        </button>
      </div>

      {/* Feedback Banner */}
      {feedback && (
        <div
          className={`p-4 rounded-2xl border text-xs font-medium flex items-center justify-between gap-3 ${
            feedback.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : feedback.type === 'error'
              ? 'bg-rose-50 border-rose-200 text-rose-900'
              : 'bg-blue-50 border-blue-200 text-blue-900'
          }`}
        >
          <div className="flex items-center gap-2.5">
            {feedback.type === 'success' && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
            {feedback.type === 'error' && <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />}
            {feedback.type === 'info' && <Info className="w-4 h-4 text-blue-600 shrink-0" />}
            <span>{feedback.text}</span>
          </div>
          <button onClick={() => setFeedback(null)} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* System Subdomain Card */}
      <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-xs space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
              Varsayılan Adiosoft Alan Adı
            </span>
            <span className="text-xs text-slate-500">
              Bu adres sitenizin her zaman çalışan ana sistem adresidir.
            </span>
          </div>
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            Her Zaman Aktif
          </span>
        </div>

        <div className="font-mono text-sm sm:text-base font-bold text-slate-900 bg-slate-50 p-3.5 rounded-xl border border-slate-200 flex items-center justify-between">
          <span>{getSitePublicUrl(activeSite)}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleCopy(getSitePublicUrl(activeSite), 'subdomain')}
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition"
              title="Kopyala"
            >
              {copiedKey === 'subdomain' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </button>
            <a
              href={getSitePublicUrl(activeSite)}
              target="_blank"
              rel="noreferrer"
              className="p-1.5 text-slate-400 hover:text-teal-600 rounded-lg hover:bg-slate-100 transition"
              title="Yeni sekmede aç"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Custom Domains List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">Bağlı Özel Alan Adları</h2>
          <span className="text-xs text-slate-500 font-medium">Toplam: {domains.length} Domain</span>
        </div>

        {domains.length === 0 ? (
          <div className="p-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 space-y-3">
            <Globe className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="text-sm font-semibold text-slate-700">Henüz özel bir alan adı bağlanmadı</p>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Sitenizin kurumsal görünmesi için kendi domain adınızı (örneğin: firmam.com) ekleyebilirsiniz.
            </p>
            <button
              onClick={() => setIsConnectModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-xs transition mt-2"
            >
              <Plus className="w-3.5 h-3.5" />
              Özel Domain Bağla
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {domains.map((dom) => {
              const isVerifyingThis = verifyingDomainId === dom.id;
              const isRemovingThis = removingDomainName === dom.domain;
              const checkInfo = dnsCheckDetails[dom.id];
              const domInstructions: FormattedDnsInstruction[] =
                (persistedInstructions[dom.domain]?.length)
                  ? persistedInstructions[dom.domain]
                  : (dom.id && persistedInstructions[dom.id]?.length)
                  ? persistedInstructions[dom.id]
                  : (checkInfo?.dnsInstructions?.length)
                  ? checkInfo.dnsInstructions
                  : (dom.dnsInstructions || []);

              return (
                <div
                  key={dom.id}
                  className={`p-5 rounded-2xl border transition ${
                    dom.isPrimary
                      ? 'bg-white border-teal-300 shadow-sm ring-1 ring-teal-200'
                      : 'bg-white border-slate-200'
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm sm:text-base font-bold text-slate-900">
                          {dom.domain}
                        </span>
                        {dom.isPrimary && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-teal-100 text-teal-800 flex items-center gap-1">
                            <Star className="w-3 h-3 fill-teal-600 text-teal-600" />
                            Birincil (Canonical)
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono text-slate-500">
                        <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-sans font-medium">
                          Host: <strong className="font-mono">{checkInfo?.firebaseStatus?.hostState || dom.hostState || 'HOST_UNHOSTED'}</strong>
                        </span>
                        <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-sans font-medium">
                          Mülkiyet: <strong className="font-mono">{checkInfo?.firebaseStatus?.ownershipState || dom.ownershipState || 'OWNERSHIP_UNVERIFIED'}</strong>
                        </span>
                        <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-sans font-medium">
                          SSL: <strong className="font-mono">{checkInfo?.firebaseStatus?.sslStatus || dom.sslStatus || 'PENDING'}</strong>
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center flex-wrap gap-2">
                      {getStatusBadge(dom)}

                      <button
                        onClick={() => {
                          setActiveDomainForGuide(dom.domain);
                          document.getElementById('dns-guide-section')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className={`px-3 py-1.5 rounded-xl border font-bold text-xs transition flex items-center gap-1.5 ${
                          selectedDomainName === dom.domain
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'border-teal-200 bg-teal-50 hover:bg-teal-100 text-teal-800'
                        }`}
                        title="Bu domainin DNS kayıtlarını aşağıda görüntüle"
                      >
                        <Server className="w-3.5 h-3.5" />
                        <span>Kayıtları Gör</span>
                      </button>

                      <button
                        onClick={() => handleVerifyDomain(dom.id, dom.domain)}
                        disabled={isVerifyingThis}
                        className="px-3 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-xs transition flex items-center gap-1.5 disabled:opacity-50"
                        title="DNS kayıtlarını şimdi sorgula"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isVerifyingThis ? 'animate-spin text-teal-600' : ''}`} />
                        <span>{isVerifyingThis ? 'Doğrulanıyor...' : 'Doğrula'}</span>
                      </button>

                      {!dom.isPrimary && (
                        <button
                          onClick={() => handleSetPrimary(dom.id, dom.domain)}
                          className="px-3 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-xs transition flex items-center gap-1.5"
                          title="Bu domaini birincil (canonical) adres yap"
                        >
                          <Star className="w-3.5 h-3.5 text-amber-500" />
                          <span>Birincil Yap</span>
                        </button>
                      )}

                      <button
                        onClick={() => handleRemoveDomain(dom.domain)}
                        disabled={isRemovingThis}
                        className="p-1.5 rounded-xl text-rose-500 hover:text-rose-700 hover:bg-rose-50 transition"
                        title="Alan Adını Kaldır"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Real Required DNS Records Section */}
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                        <Server className="w-3.5 h-3.5 text-teal-600" />
                        Gerekli DNS kayıtları
                        {domInstructions.length > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 text-[10px] font-mono">
                            {domInstructions.length} Kayıt
                          </span>
                        )}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono">
                        Yayın Durumu: Aktif
                      </span>
                    </div>

                    {domInstructions.length === 0 ? (
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-500">
                        Yayın altyapısından gereken DNS kayıtları alınıyor... Lütfen 'Doğrula' butonuna basarak güncel durumu sorgulayın.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                        {domInstructions.map((rec, rIdx) => {
                          const copyKeyHost = `${dom.id}_${rIdx}_host`;
                          const copyKeyValue = `${dom.id}_${rIdx}_val`;
                          return (
                            <div
                              key={rIdx}
                              className={`p-3 rounded-xl border text-xs font-mono space-y-1.5 ${
                                rec.status === 'CONFIGURED'
                                  ? 'bg-emerald-50/50 border-emerald-200 text-emerald-950'
                                  : rec.status === 'MISMATCH'
                                  ? 'bg-rose-50/50 border-rose-200 text-rose-950'
                                  : 'bg-slate-50 border-slate-200 text-slate-900'
                              }`}
                            >
                              <div className="flex items-center justify-between font-sans">
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-slate-900 text-white">
                                  {rec.type}
                                </span>
                                {rec.status === 'CONFIGURED' ? (
                                  <span className="text-[10px] font-bold text-emerald-700 flex items-center gap-1">
                                    <Check className="w-3 h-3" /> Eşleşti
                                  </span>
                                ) : rec.status === 'MISMATCH' ? (
                                  <span className="text-[10px] font-bold text-rose-700 flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" /> Uyuşmazlık
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-medium text-amber-700 flex items-center gap-1">
                                    <RefreshCw className="w-2.5 h-2.5 animate-spin" /> Bekleniyor
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center justify-between gap-1 text-[11px]">
                                <span className="text-slate-500">Host:</span>
                                <div className="flex items-center gap-1 font-bold">
                                  <span>{rec.host}</span>
                                  <button
                                    onClick={() => handleCopy(rec.host, copyKeyHost)}
                                    className="p-0.5 hover:bg-black/5 rounded text-slate-400 hover:text-slate-700"
                                    title="Host Kopyala"
                                  >
                                    {copiedKey === copyKeyHost ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                                  </button>
                                </div>
                              </div>

                              <div className="flex items-start justify-between gap-1 text-[11px]">
                                <span className="text-slate-500 shrink-0">Değer:</span>
                                <div className="flex items-center gap-1 break-all text-right font-medium text-slate-800">
                                  <span className="truncate max-w-[170px]" title={rec.value}>{rec.value}</span>
                                  <button
                                    onClick={() => handleCopy(rec.value, copyKeyValue)}
                                    className="p-0.5 hover:bg-black/5 rounded text-slate-400 hover:text-slate-700 shrink-0"
                                    title="Değeri Kopyala"
                                  >
                                    {copiedKey === copyKeyValue ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                                  </button>
                                </div>
                              </div>

                              {rec.currentValue && rec.status === 'MISMATCH' && (
                                <div className="text-[10px] text-rose-700 pt-1 border-t border-rose-100 font-sans">
                                  Mevcut DNS Değeri: <span className="font-mono font-bold">{rec.currentValue}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* If DNS verification returned error or info */}
                  {checkInfo && !checkInfo.isPropagated && checkInfo.error && (
                    <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 space-y-1">
                      <div className="font-bold flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                        <span>DNS Durum Detayı:</span>
                      </div>
                      <p>{checkInfo.error}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* DNS Configuration Guide */}
      <div id="dns-guide-section" className="p-6 sm:p-7 bg-slate-900 text-white rounded-2xl border border-slate-800 shadow-lg space-y-6 scroll-mt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2">
              <Server className="w-4 h-4 text-teal-400" />
              Gerekli DNS Kayıtları (Firebase Hosting)
              {selectedDomainName && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-300 font-mono">
                  {selectedDomainName}
                </span>
              )}
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Alan adınızı yönettiğiniz panele (GoDaddy, Natro, Turhost, İsimtescil vb.) giriş yapın ve Firebase altyapımız tarafından istenen DNS kayıtlarını eksiksiz tanımlayın:
            </p>
          </div>

          {domains.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5 self-start sm:self-auto bg-slate-800 p-1 rounded-xl border border-slate-700">
              {domains.map((d) => (
                <button
                  key={d.id || d.domain}
                  type="button"
                  onClick={() => setActiveDomainForGuide(d.domain || null)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-mono font-semibold transition ${
                    selectedDomainName === d.domain
                      ? 'bg-teal-600 text-white shadow-xs'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {d.domain}
                </button>
              ))}
            </div>
          )}
        </div>

        {guideInstructions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs font-mono">
            {guideInstructions.map((rec, idx) => {
              const copyKeyVal = `guide_val_${idx}`;
              const copyKeyHost = `guide_host_${idx}`;
              return (
                <div key={idx} className="p-4 bg-slate-800/90 rounded-xl border border-slate-700 space-y-2">
                  <div className="flex items-center justify-between text-teal-400 font-bold font-sans">
                    <span className="flex items-center gap-1.5">
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-teal-500/20 text-teal-300 font-mono">
                        {rec.type} Kaydı
                      </span>
                      <span className="text-[11px] text-slate-300 font-normal">
                        ({rec.type === 'TXT' ? 'Mülkiyet/SSL Doğrulama' : 'Yönlendirme'})
                      </span>
                    </span>
                    <button
                      onClick={() => handleCopy(rec.value, copyKeyVal)}
                      className="p-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 transition flex items-center gap-1 text-[10px]"
                    >
                      {copiedKey === copyKeyVal ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      Kopyala
                    </button>
                  </div>
                  <div className="space-y-1.5 text-slate-300 text-[11px]">
                    <div className="flex justify-between items-center">
                      <span><strong>Tür:</strong> {rec.type}</span>
                      <span className="text-[10px] text-slate-400">TTL: Otomatik</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span><strong>Host / Ad:</strong> <span className="text-teal-300">{rec.host}</span></span>
                      <button
                        onClick={() => handleCopy(rec.host, copyKeyHost)}
                        className="text-[10px] text-slate-400 hover:text-slate-200"
                      >
                        {copiedKey === copyKeyHost ? 'Kopyalandı' : 'Host Kopyala'}
                      </button>
                    </div>
                    <div className="break-all">
                      <strong>Değer:</strong> <span className="text-teal-300">{rec.value}</span>
                    </div>
                    {rec.description && (
                      <p className="text-[10px] text-slate-400 font-sans pt-1 border-t border-slate-700/60">
                        {rec.description}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-5 bg-slate-800/60 rounded-xl border border-slate-700 text-center space-y-2">
            <Globe className="w-8 h-8 text-teal-400 mx-auto" />
            <p className="text-xs font-semibold text-slate-200">
              {domains.length === 0
                ? "Özel alan adınızı yukarıdaki 'Özel Domain Bağla' butonuna tıklayarak ekleyin."
                : "Yayın altyapısından gereken DNS kayıtları alınıyor..."}
            </p>
            <p className="text-[11px] text-slate-400 max-w-md mx-auto">
              Alan adınız Firebase bulut altyapımıza kaydedildiğinde, sunucularımız tarafından üretilen kesin DNS kayıtları (A ve TXT doğrulama) burada listelenecektir.
            </p>
          </div>
        )}

        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/60 flex items-center gap-3 text-xs text-slate-300">
          <Lock className="w-5 h-5 text-emerald-400 shrink-0" />
          <span>
            DNS kayıtlarınız doğrulandığı anda 256-Bit SSL güvenlik sertifikası <strong>tamamen ücretsiz ve otomatik</strong> olarak aktif edilir.
          </span>
        </div>
      </div>

      {/* Connect Domain Modal */}
      {isConnectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 space-y-5 animate-in zoom-in-95">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
                  <Globe className="w-4 h-4" />
                </div>
                <h3 className="text-base font-bold text-slate-900">
                  {modalStep === 'SUCCESS' ? 'DNS Kayıtları Hazır' : 'Özel Alan Adı Bağla'}
                </h3>
              </div>
              <button
                onClick={() => {
                  setIsConnectModalOpen(false);
                  setModalStep('FORM');
                }}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {modalStep === 'FORM' ? (
              <form onSubmit={handleConnectDomain} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-800">Domain Adresiniz</label>
                  <input
                    type="text"
                    placeholder="ornekalanadi.com"
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-mono bg-white text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-teal-500 outline-none"
                    autoFocus
                    required
                  />
                  <span className="text-[11px] text-slate-500 block">
                    'https://' veya 'http://' yazmanıza gerek yoktur.
                  </span>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoAddWww}
                      onChange={(e) => setAutoAddWww(e.target.checked)}
                      className="rounded text-teal-600 focus:ring-teal-500"
                    />
                    <span>Otomatik olarak 'www' varyantını da ekle ve bağla</span>
                  </label>
                </div>

                <div className="flex justify-end gap-2.5 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsConnectModalOpen(false)}
                    className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !domainInput.trim()}
                    className="px-5 py-2 rounded-xl bg-teal-600 hover:bg-teal-500 text-white text-xs font-bold shadow-xs transition flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        <span>Kaydediliyor...</span>
                      </>
                    ) : (
                      <>
                        <span>Domaini Kaydet & Devam Et</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">Alan adı başarıyla kaydedildi: {modalAddedDomain}</span>
                    <p className="text-[11px] text-emerald-800 mt-0.5">
                      Lütfen alan adı yönetim panelinize (GoDaddy, Natro, Turhost vb.) giderek aşağıdaki kayıtları eksiksiz tanımlayın:
                    </p>
                  </div>
                </div>

                <div className="space-y-2.5 max-h-[320px] overflow-y-auto pr-1">
                  {modalAddedInstructions.map((rec, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-1.5 font-mono">
                      <div className="flex items-center justify-between font-sans">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-teal-100 text-teal-800">
                          {rec.type} Kaydı
                        </span>
                        <span className="text-[10px] text-slate-500 font-normal">
                          {rec.type === 'TXT' ? 'Mülkiyet Doğrulama' : 'IP Yönlendirme'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-1 text-[11px] font-sans">
                        <span className="text-slate-500">Host / Ad:</span>
                        <div className="flex items-center gap-1">
                          <code className="text-teal-700 font-mono font-bold bg-white px-1.5 py-0.5 rounded border border-slate-200">{rec.host}</code>
                          <button
                            type="button"
                            onClick={() => handleCopy(rec.host, `modal_host_${idx}`)}
                            className="p-1 hover:bg-slate-200 rounded text-slate-500"
                            title="Host Kopyala"
                          >
                            {copiedKey === `modal_host_${idx}` ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-1 text-[11px] font-sans">
                        <span className="text-slate-500 shrink-0">Değer:</span>
                        <div className="flex items-center gap-1 min-w-0">
                          <code className="text-teal-700 font-mono font-bold bg-white px-1.5 py-0.5 rounded border border-slate-200 truncate max-w-[200px]" title={rec.value}>{rec.value}</code>
                          <button
                            type="button"
                            onClick={() => handleCopy(rec.value, `modal_val_${idx}`)}
                            className="p-1 hover:bg-slate-200 rounded text-slate-500 shrink-0"
                            title="Değeri Kopyala"
                          >
                            {copiedKey === `modal_val_${idx}` ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                      {rec.description && (
                        <p className="text-[10px] text-slate-500 font-sans pt-1 border-t border-slate-200">
                          {rec.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsConnectModalOpen(false);
                      setModalStep('FORM');
                      setDomainInput('');
                    }}
                    className="w-full py-2.5 rounded-xl bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs shadow-xs transition"
                  >
                    Kayıtları Gördüm & Tamamla
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog
        open={deleteDialog.open}
        title="Alan Adını Kaldırmak İstediğinize Emin Misiniz?"
        description={`"${deleteDialog.domainName}" alan adı bağlantısı bu web sitesinden kaldırılacaktır. Bu işlem geri alınamaz.`}
        variant="destructive"
        confirmText={deleteDialog.loading ? 'Kaldırılıyor...' : 'Evet, Kaldır'}
        cancelText="Vazgeç"
        loading={deleteDialog.loading}
        onConfirm={handleExecuteRemoveDomain}
        onCancel={() => {
          if (!deleteDialog.loading) {
            setDeleteDialog({ open: false, domainName: '', loading: false });
          }
        }}
      />
    </div>
  );
};
